import { useCallback, useEffect, useState } from 'react';

import { persistLocale, resolveInitialLocale } from './locale-preference';
import { type Locale } from './locales';
import { directionFor } from './rtl';

export interface UseLocalePreferenceResult {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
}

/**
 * React binding over `locale-preference.ts`'s framework-agnostic core —
 * the exact division of responsibility `@ecom/ui-kit`'s `use-theme.ts`
 * already established for `data-theme`. For the merchant and admin
 * dashboards: reads a persisted choice on mount (falling back to
 * `DEFAULT_LOCALE`), keeps `<html lang>`/`dir` in sync as a side effect
 * (these are plain Vite SPAs — nothing else ever sets those attributes),
 * and persists every explicit change.
 */
export function useLocalePreference(): UseLocalePreferenceResult {
  const [locale, setLocaleState] = useState<Locale>(() => resolveInitialLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionFor(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    persistLocale(next);
  }, []);

  return { locale, setLocale };
}
